import React, { RefObject, useEffect } from 'react';
import {
  IChartingLibraryWidget,
  ResolutionString,
  SeriesType,
} from '../../charting_library/charting_library';
import { captureError } from '../../lib/errorTracking';
import { tryCatch } from '../../lib/tryCatch';

type StateToChartResolutionSyncProps = {
  widgetRef: RefObject<IChartingLibraryWidget | null>;
  isChartReady: () => boolean;
  doesSymbolMatch: () => boolean;
  chartResolution: ResolutionString;
  setChartResolution: (resolution: ResolutionString) => void;
  setChartType: (chartType: SeriesType) => void;
};

/**
 * Listens to resolution/chart type changes in the TradingView chart UI
 * and syncs them back to React state
 */
export const StateToChartResolutionSync: React.FC<StateToChartResolutionSyncProps> = ({
  widgetRef,
  isChartReady,
  doesSymbolMatch,
  chartResolution,
  setChartResolution,
  setChartType,
}) => {
  useEffect(() => {
    if (!isChartReady()) {
      return;
    }

    const chart = tryCatch(() => widgetRef.current?.activeChart());
    if (!doesSymbolMatch() || !chart) {
      return;
    }

    // We need to use chart.onDataLoaded() to handle the case where the user changes the resolution
    // rather than using chart.onIntervalChanged() because the onIntervalChanged fires right when the user changes the resolution,
    // but the chart is not fully rendered or ready on the new resolution until the onDataLoaded event fires
    tryCatch(() => {
      const dataLoadedSubManager = chart.onDataLoaded();

      // Symbol changed, unsubscribe old listener and subscribe to the new one.
      dataLoadedSubManager.unsubscribeAll(null);
      dataLoadedSubManager.subscribe(null, () => {
        tryCatch(() => {
          const resolution = chart.resolution();
          setChartResolution(resolution);
          const chartType = chart.chartType();
          setChartType(chartType);
        });
      });
    });
  }, [chartResolution, doesSymbolMatch, isChartReady, setChartResolution, setChartType, widgetRef]);

  return null;
};

type ChartToStateResolutionSyncProps = {
  widgetRef: RefObject<IChartingLibraryWidget | null>;
  isChartReady: () => boolean;
  doesSymbolMatch: () => boolean;
  chartResolution: ResolutionString;
  chartType: SeriesType;
  isLoading: boolean;
  onResolutionChangeStart?: () => void;
  onResolutionChangeComplete?: () => void;
};

/**
 * Syncs React state changes (chartResolution, chartType props) to the TradingView chart
 * when they change externally (e.g., from UI controls outside the chart)
 */
export const ChartToStateResolutionSync: React.FC<ChartToStateResolutionSyncProps> = ({
  widgetRef,
  isChartReady,
  doesSymbolMatch,
  chartResolution,
  chartType,
  isLoading,
  onResolutionChangeStart,
  onResolutionChangeComplete,
}) => {
  // Sync resolution from state to chart
  useEffect(() => {
    if (!isChartReady()) return;

    const chart = tryCatch(() => widgetRef.current?.activeChart());
    if (!doesSymbolMatch() || !chart) return;
    if (chartResolution === chart.resolution()) {
      return;
    }

    onResolutionChangeStart?.();
    chart
      .setResolution(chartResolution, {
        dataReady: () => {
          onResolutionChangeComplete?.();
        },
      })
      .catch((error) => {
        captureError(error);
        onResolutionChangeComplete?.();
      });
  }, [
    isLoading,
    chartResolution,
    doesSymbolMatch,
    isChartReady,
    widgetRef,
    onResolutionChangeStart,
    onResolutionChangeComplete,
  ]);

  // Sync chart type from state to chart
  useEffect(() => {
    if (!isChartReady()) return;

    const chart = tryCatch(() => widgetRef.current?.activeChart());
    if (!doesSymbolMatch() || !chart) return;

    tryCatch(() => chart.setChartType(chartType));
  }, [isLoading, widgetRef, isChartReady, doesSymbolMatch, chartType]);

  return null;
};
